import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { NavController } from '@ionic/angular';
import { gql } from 'apollo-angular';
import { Deploy } from 'cordova-plugin-ionic';
import { BehaviorSubject, distinctUntilChanged, Observable } from 'rxjs';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { AnalyticsServicesNameEnum } from 'src/app/core/services/analytics/enums/analytics-services-name.enum';
import { EventTrackingEnum } from 'src/app/shared/enums/event-tracking.enum';
import { AccountService } from 'src/app/shared/services/account.service';
import { AuthTokenService } from 'src/app/shared/services/auth-token.service';
import { ApiInterfaceService } from './api-interface.service';
import { NativeHttpProvider } from './native-http.provider';
import { StorageService } from './storage.service';

export type LoginDetails = {
  email: string;
  password: string;
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // Used to indicate that the service has infact loaded and all auth information when called upon will be correctly representing the state
  private isInitialisedSubject = new BehaviorSubject<Readonly<boolean>>(false);

  // Used to indicate the users auth status
  userAuthStatusSubject = new BehaviorSubject<Readonly<boolean>>(false);

  // Helps handle our JWT tokens from BE
  private jwtHelper: JwtHelperService = new JwtHelperService();

  constructor(
    private storage: StorageService,
    private accountService: AccountService,
    private api: ApiInterfaceService,
    private navCtrl: NavController,
    private nativeHttp: NativeHttpProvider,
    private authTokenService: AuthTokenService,
    private analyticsService: AnalyticsService,
  ) {
    this.markUserLoginStatus();
    this.isUserLoggedIn$.pipe(distinctUntilChanged()).subscribe((isLoggedIn) => {
      if (isLoggedIn) {
        this.accountService.initialiseAccountData();
      } else {
        // Call the analytics callback when the logged in user changed
        // this is called here rather than in the analytics service to prevent circular dependencies
        // used to reset the user identify queues and segment calls
        this.analyticsService.loggedInUserChanged();
        this.accountService.resetAccountData();
      }
    });
  }

  /**
   * Public getter for observable from private isInitialised.
   */
  get isInitialised$(): Observable<boolean> {
    return this.isInitialisedSubject.asObservable();
  }

  /**
   * Public getter for observable from private isUserLoggedInSubject.
   */
  get isUserLoggedIn$(): Observable<boolean> {
    return this.userAuthStatusSubject.asObservable();
  }

  // new auth methods below:

  /**
   * Marks user login status and updates it in the app. Once logged in, sets the is initialised status
   * using the `isInitialisedSubject`.
   */
  async markUserLoginStatus(): Promise<void> {
    // make sure storage is ready
    await this.storage.ready();

    await this.markUserAsLoggedIn();

    // Let calling services know that the IndexDB has loaded and auth information is ready to be pulled
    this.isInitialisedSubject.next(true);
  }

  /**
   * Makes a login request with the provided credentials.
   * Updates the state of the application upon successful login.
   * Returns if login was successful or not.
   *
   * @param details - User's email and password.
   * @returns Promise<boolean>. True if token exists, false otherwise
   */
  async login(details: LoginDetails): Promise<boolean> {
    try {
      // The signin API only works with lowercase email addresses
      details.email = details.email.toLowerCase();
      const response = await (
        await this.api.post(
          '/api/ionic_authentication/sign_in.json',
          { email: details.email, password: details.password },
          false,
        )
      ).toPromise();

      if (response) {
        this.authTokenService.setAccessToken(response.access_token);
        await this.authTokenService.setRefreshToken(response.refresh_token);
        await this.markUserAsLoggedIn();
        this.analyticsTrackLogin();
        return true;
      }
      return false;
    } catch (e) {
      // login error
      return Promise.reject(e);
    }
  }

  async markUserAsLoggedIn() {
    this.nativeHttp.clearCookies(); // Since our old cookie may contain information about the previous saved token
    const isLoggedIn = this.isUserLoggedIn(); // will check the auth status and update the auth status subject.

    // Once the user is logged in get the current and target users.
    if (isLoggedIn) {
      await this.accountService.updateCurrentAndTargetUser();
    }

    this.userAuthStatusSubject.next(isLoggedIn);
  }

  /**
   * Removes the auth token from local storage and navigates the user to root.
   */
  async logout(): Promise<void> {
    console.log('[LOGOUT CALLED]');
    await this.authTokenService.resetTokens();
    this.nativeHttp.clearCookies();
    this.userAuthStatusSubject.next(false);
    this.navCtrl.navigateRoot('login');
  }

  /**
   * Make the API call to refresh the user's token and update the stored token with the new token we get back.
   */
  async refreshAccessToken(): Promise<void> {
    // make a request to get the new (refreshed) token
    const access_token = await this.authTokenService.refreshToken();
    try {
      const res = await (
        await this.api.get('/api/ionic_authentication/authentication_token', false, { access_token }, false)
      ).toPromise();

      if (!res['token']) {
        // make sure we have received a token!
        throw new Error('Response token is empty');
      }

      // update the currently signed in user with the new parts of the received token token (authentication_token and expiry)
      const newToken = res['token'];
      const newTokenDecoded = this.jwtHelper.decodeToken(newToken);
      const user = this.accountService.getUser();

      this.analyticsService.track('Access Token Refreshed', {}, AnalyticsServicesNameEnum.SEGMENT_SERVICE);
      console.log('[refreshToken user and new token]', !!user, !!newTokenDecoded);
      this.authTokenService.setAccessToken(newToken);
      await this.markUserAsLoggedIn();
    } catch (error) {
      console.error('[refreshToken] failed with', error, 'access_token_exists', !!access_token);
      // token refresh failed, sign them out
      await this.logout();
      throw error;
    }
  }

  /**
   * Checks if a user access token exists or not.
   *
   * @returns True if user access token is found, false otherwise.
   */
  isUserLoggedIn(): boolean {
    // if access token is present, we consider user has logged in
    return !!this.authTokenService.accessToken;
  }

  /**
   * Allow admin to login as client or worker - This should only be called if logged in as admin.
   *
   * @param permalink - Link for the user to login with.
   */
  async loginAs(permalink): Promise<void> {
    try {
      const tokenResponse: any = await (
        await this.api.get(`/login_as/${permalink}.json`, true, null, true)
      ).toPromise();
      // UserAuthStatusSubject subscriptions with distinctUntilChanged wont be fired for second user if we log in two users back to back.
      // So emitting false before we emit the next true
      this.userAuthStatusSubject.next(false);
      await this.accountService.setTargetUserAndUUID(tokenResponse['target_user_details']);
      await this.markUserAsLoggedIn();
      Deploy.reloadApp();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Resent account unlock instruction to email.
   *
   * @param email - registered email address.
   */
  async resetUnlockInstruction(email: string): Promise<string> {
    try {
      const res = await this.api.gqlMutation(
        gql`
          mutation ($email: String!) {
            resendUnlockInstructions(input: { email: $email }) {
              response
            }
          }
        `,
        { email },
      );
      return res['resendUnlockInstructions']['response'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Track user login in our analytics.
   */
  analyticsTrackLogin(): void {
    // Send an analytics log event if a user login successfully
    // Emit the Firebase version of these events in a different step as they are named differently for GA4
    this.analyticsService.track('login', {}, AnalyticsServicesNameEnum.FIREBASE_ANALYTICS_SERVICE);
    this.analyticsService.track(EventTrackingEnum.LOGGED_IN, {}, AnalyticsServicesNameEnum.SEGMENT_SERVICE);
  }
}
